/**
 * Utility functions for extracting and formatting error messages from API responses
 */

/**
 * Extracts a user-friendly error message from an API error response
 * Handles various error response structures:
 * - error.response.data.message (top-level message)
 * - error.response.data.data.message (nested message)
 * - error.response.data.data.error.message (nested error message)
 * - error.response.data.error.message (error object message)
 * - error.message (fallback)
 * 
 * @param error - The error object from a catch block
 * @param defaultMessage - Optional default message if no error message is found
 * @returns A user-friendly error message string
 */
export const extractErrorMessage = (
    error: any,
    defaultMessage: string = 'An unexpected error occurred. Please try again.'
): string => {
    // Handle network errors
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        return 'Cannot connect to server. Please check your internet connection and try again.';
    }

    // Handle cancelled requests
    if (error.code === 'ERR_CANCELED' || error.message?.includes('canceled')) {
        return 'Request was cancelled.';
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return 'Request timed out. Please try again.';
    }

    // Extract from response data structure
    if (error.response?.data) {
        const responseData = error.response.data;

        // Generic HTTP status labels that aren't useful to display
        const genericMessages = new Set([
            'Internal Server Error',
            'Internal server error',
            'Bad Request',
            'Bad request',
            'Unauthorized',
            'Forbidden',
            'Not Found',
            'Not found',
            'Validation Failed',
            'Validation failed',
            'Server Error',
            'Server error',
        ]);

        // 1. Top-level message wins when present and non-generic.
        // The backend's Api.badRequest/unauthorized/notFound/etc. helpers
        // always set this field to the specific human-readable error.
        if (responseData.message && !genericMessages.has(responseData.message)) {
            return responseData.message;
        }

        // 2. Specific nested error message (covers 500s where top-level says
        // "Internal server error" but data.error.message has the real cause).
        if (responseData.data?.error?.message) {
            return responseData.data.error.message;
        }

        // 3. Top-level error object message.
        if (responseData.error?.message) {
            return responseData.error.message;
        }

        // 4. Nested data.message (non-generic).
        if (responseData.data?.message && !genericMessages.has(responseData.data.message)) {
            return responseData.data.message;
        }

        // 5. Validation errors array.
        if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
            const first = responseData.errors[0];
            if (typeof first === 'string') return first;
            if (first && typeof first.message === 'string') return first.message;
        }

        // 6. Validation errors object.
        if (responseData.errors && typeof responseData.errors === 'object') {
            const firstError = Object.values(responseData.errors)[0];
            if (typeof firstError === 'string') {
                return firstError;
            }
            if (Array.isArray(firstError) && firstError.length > 0) {
                return firstError[0];
            }
        }

        // 7. Nested validation errors array (data.errors).
        if (Array.isArray(responseData.data?.errors) && responseData.data.errors.length > 0) {
            const first = responseData.data.errors[0];
            if (typeof first === 'string') return first;
            if (first && typeof first.message === 'string') return first.message;
        }

        // 8. Last resort: return the top-level message even if generic.
        if (responseData.message) {
            return responseData.message;
        }
    }

    // Fallback to error message
    if (error.message) {
        return error.message;
    }

    // Last resort: default message
    return defaultMessage;
};

/**
 * Extracts error details including message, status code, and error code
 * @param error - The error object from a catch block
 * @returns An object with error details
 */
export const extractErrorDetails = (error: any): {
    message: string;
    statusCode?: number;
    errorCode?: string;
    errors?: any;
} => {
    const message = extractErrorMessage(error);
    const statusCode = error.response?.status;
    const errorCode = error.response?.data?.errorCode || error.response?.data?.code;
    const errors = error.response?.data?.errors || error.response?.data?.data?.errors;

    return {
        message,
        statusCode,
        errorCode,
        errors
    };
};

/**
 * Formats an error for console/Sentry logging without leaking the request.
 *
 * Never pass a raw AxiosError to console.error: Sentry's consoleLoggingIntegration
 * serializes arguments with normalize(value, 3) and calls AxiosError.toJSON(), which
 * preserves `config.url` (query-string access tokens) and `config.data` (the full
 * request body - passwords, emails, register numbers) as plain strings. This helper
 * keeps only the status/code/message triple, which is what the handlers actually need.
 *
 * @param error - The error object from a catch block
 * @returns A single-line, log-safe summary string
 */
export const describeErrorForLog = (error: any): string => {
    if (error == null) return 'unknown error';

    const parts: string[] = [];
    const status = error?.response?.status;
    const code = error?.code || error?.response?.data?.errorCode;

    if (status !== undefined) parts.push(`status=${status}`);
    if (code) parts.push(`code=${code}`);

    const message =
        typeof error === 'string'
            ? error
            : error?.response?.data?.message ||
              error?.response?.data?.data?.message ||
              error?.message ||
              (error instanceof Error ? error.name : 'unknown error');

    parts.push(`message=${String(message).slice(0, 300)}`);

    return parts.join(' ');
};

/**
 * Checks if an error is a specific HTTP status code
 * @param error - The error object
 * @param statusCode - The status code to check for
 * @returns True if the error matches the status code
 */
export const isErrorStatus = (error: any, statusCode: number): boolean => {
    return error.response?.status === statusCode;
};

/**
 * Checks if an error is a network error
 * @param error - The error object
 * @returns True if it's a network error
 */
export const isNetworkError = (error: any): boolean => {
    return error.code === 'ERR_NETWORK' || error.message === 'Network Error';
};

/**
 * Checks if an error is a validation error (422)
 * @param error - The error object
 * @returns True if it's a validation error
 */
export const isValidationError = (error: any): boolean => {
    return error.response?.status === 422;
};

/**
 * Checks if an error is an authentication error (401)
 * @param error - The error object
 * @returns True if it's an authentication error
 */
export const isAuthError = (error: any): boolean => {
    return error.response?.status === 401;
};

/**
 * Checks if an error is a forbidden error (403)
 * @param error - The error object
 * @returns True if it's a forbidden error
 */
export const isForbiddenError = (error: any): boolean => {
    return error.response?.status === 403;
};

/**
 * Checks if an error is a not found error (404)
 * @param error - The error object
 * @returns True if it's a not found error
 */
export const isNotFoundError = (error: any): boolean => {
    return error.response?.status === 404;
};

